import Vue from 'vue'
import Router from 'vue-router'
import HomePage from '@/components/HomePage'
import api from '@/api'
import { getSSOAuthUrl, pkceUtils } from '../utils/http'
import axios from 'axios'
import config from '../utils/config'

Vue.use(Router)

// SSO配置
let ssoConfig = {
  enabled: true, // 默认启用SSO
  ssoUrl: '', // 从配置文件获取
  clientId: config.sso.clientId // 从配置文件获取
}

// 获取SSO配置
async function getSSOConfig() {
  try {
    const response = await api.getSSOConfig()
    if (response && response.data) {
      const config = response.data
      ssoConfig = {
        enabled: config.ssoSikp !== 'true', // 如果ssoSikp为true，则禁用SSO
        ssoUrl: config.ssoUrl || ssoConfig.ssoUrl,
        clientId: config.clientId || ssoConfig.clientId
      }
      //console.log('已获取SSO配置', ssoConfig)
    }
  } catch (error) {
    console.error('获取SSO配置失败', error)
  }
  return ssoConfig
}

const router = new Router({
  routes: [
    {
      path: '/',
      name: '首页',
      component: HomePage,
      redirect:'ProductManagement',
      children:[
        {
          path:'/ProductManagement',
          name:'产品管理',
          component:()=>import('@/views/ProductManagement.vue')
        },{
          path:'/MaterialManagement',
          name:'物料管理',
          component:()=>import('@/views/MaterialManagement.vue')
        },{
          path:'/StoreManagement',
          name:'店铺管理',
          component:()=>import('@/views/StoreManagement.vue')
        }
      ]
    },{
      path:'/login',
      name:'登录',
      component:()=>import('@/views/Login.vue')
    },
    {
      path:'*',
      component:()=>import('@/views/404.vue')
    }
  ]
})
/* 路由跳转前 */
router.beforeEach(async (to, from, next) => {
  //console.log('路由守卫触发 - 目标路径:', to.path)
  let token = localStorage.getItem('token')
  //console.log('当前token状态:', token ? '有token' : '无token')

  // 检查是否有token无效标记
  const tokenInvalid = localStorage.getItem('token_invalid') === 'true'
  if (tokenInvalid) {
    //console.log('检测到token无效标记，清除认证信息')
    localStorage.removeItem('token')
    localStorage.removeItem('token_invalid')
    token = null
  }

  // 获取SSO配置
  const config = await getSSOConfig()

  // 检查URL中是否包含SSO认证返回的code
  const urlParams = new URLSearchParams(window.location.search)
  const ssoCode = urlParams.get('code')
  //console.log('SSO认证返回的code:', ssoCode)

  // 检查存储的code_verifier
  const savedCodeVerifier = pkceUtils.getCodeVerifier()
  //console.log('存储的code_verifier:', savedCodeVerifier)

  // 检查是否有error参数
  const errorParam = urlParams.get('error')
  const errorDescription = urlParams.get('error_description')
  if (errorParam) {
    console.error('SSO认证返回错误:', errorParam, errorDescription)
  }

  if (ssoCode) {
    //console.log('检测到SSO认证返回码，准备获取token...')

    // 确保code_verifier存在
    if (!savedCodeVerifier) {
      //console.error('没有找到code_verifier，重新生成并保存')
      // 使用默认的code_verifier
      pkceUtils.saveCodeVerifier(pkceUtils.generateCodeVerifier())
    }

    try {
      // 使用SSO认证码获取token
      //console.log('调用getSSOToken，参数:', {code: ssoCode, code_verifier: savedCodeVerifier})

      const ssoResult = await api.getSSOToken(ssoCode)
      console.log('SSO token请求结果:', ssoResult)

      if (ssoResult && ssoResult.access_token) {
        console.log('成功获取access_token:', ssoResult.access_token)
        console.log('SSO返回的完整结果:', ssoResult)

        // 直接保存token并登录
        localStorage.setItem('token', ssoResult.access_token)

        // 保存用户角色（如果存在）
        if (ssoResult.user_role) {
          console.log('保存用户角色到localStorage:', ssoResult.user_role)
          localStorage.setItem('user_role', ssoResult.user_role)
          
          // 同时保存到Vuex store
          try {
            const store = await import('@/store')
            store.default.dispatch('setUserRole', ssoResult.user_role)
            console.log('用户角色已保存到Vuex store:', ssoResult.user_role)
          } catch (e) {
            console.error('保存到Vuex store失败:', e)
          }
        } else {
          console.warn('SSO返回结果中没有user_role字段')
        }

        // 清除SSO临时数据
        localStorage.removeItem('sso_access_token')
        localStorage.removeItem('sso_username')

        // 清除URL中的code参数
        window.history.replaceState({}, document.title, '/')

        // 直接跳转到首页
        console.log('SSO登录成功，跳转到首页')
        next('/')
        return
      } else if (ssoResult && ssoResult.error) {
        // 处理SSO错误
        console.error('SSO认证失败:', ssoResult.error_description || ssoResult.error)

        // 如果是未授权用户错误，显示特定提示
        if (ssoResult.error === 'unauthorized_user') {
          // 清除URL中的code参数
          window.history.replaceState({}, document.title, '/')

          // 创建错误提示
          const Vue = await import('vue')
          const ElementUI = await import('element-ui')
          Vue.default.use(ElementUI.default)
          ElementUI.Message.error({
            message: ssoResult.error_description || '用户未授权，请联系管理员开通权限',
            duration: 5000,
            showClose: true
          })

          // 跳转到登录页或显示错误页面
          next('/login')
          return
        }

        console.error('获取token失败，接口返回:', ssoResult)
      }
    } catch (error) {
      console.error('调用getSSOToken出错:', error)
      
      // 【安全修复】处理403错误 - 用户不在数据库中
      if (error.response && error.response.status === 403) {
        console.error('用户未授权访问系统:', error.response.data)
        
        // 清除所有认证信息
        localStorage.removeItem('token')
        localStorage.removeItem('sso_access_token')
        localStorage.removeItem('sso_username')
        localStorage.removeItem('user_role')
        localStorage.removeItem('token_invalid')
        
        // 设置用户退出标记，防止再次自动跳转SSO
        localStorage.setItem('user_logged_out', 'true')
        
        // 清除URL中的code参数
        window.history.replaceState({}, document.title, '/')
        
        // 显示友好的错误提示
        const Vue = await import('vue')
        const ElementUI = await import('element-ui')
        Vue.default.use(ElementUI.default)
        
        const errorMessage = error.response.data && error.response.data.detail 
          ? error.response.data.detail 
          : '您的账号未授权访问此系统，请联系管理员'
        
        ElementUI.Message.error({
          message: errorMessage,
          duration: 10000,
          showClose: true
        })
        
        // 跳转到登录页
        next('/login')
        return
      }
      
      if (error.response) {
        console.error('响应状态:', error.response.status)
        console.error('响应数据:', error.response.data)
      }
    }
  }

  if(token){
    //console.log('已有token, 允许访问:', token)
    if(to.path == '/login'){
      //console.log('当前已登录，从登录页重定向到首页')
      next('/')
    }else{
      console.log('允许访问:', to.path)
      next()
    }
  }else{
    //console.log('无token或token已失效, 需要认证')
    // 检查是否是用户主动退出登录
    const userLoggedOut = localStorage.getItem('user_logged_out')

    // 如果用户主动退出，允许访问登录页面
    if (userLoggedOut === 'true') {
      if (to.path == '/login') {
        console.log('用户主动退出，显示登录页')
        next()
      } else {
        console.log('用户主动退出且访问其他页面，跳转到登录页')
        next('/login')
      }
      return
    }

    // 首次访问或token过期（非用户主动退出），自动尝试SSO认证
    if (config.enabled && !ssoCode) {
      // 获取包含PKCE参数的SSO认证URL
      const authUrl = await getSSOAuthUrl()
      console.log('首次访问或token过期，自动跳转SSO认证')
      window.location.href = authUrl
      return
    }

    // 如果SSO禁用或已经尝试SSO但失败，则跳转到登录页
    if (to.path != '/login') {
      console.log('SSO认证失败或禁用，跳转到登录页')
      next('/login')
    } else {
      console.log('允许访问登录页')
      next()
    }
  }
  let preTitle = to.name || 'Haleon 智能产品管理助手'
  document.title = preTitle
})

const originalPush = Router.prototype.push

Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

Router.prototype.replace = function push(location) {
  return Router.prototype.replace.call(this, location).catch(err => err)
}

export default router



// WEBPACK FOOTER //
// ./src/router/index.js